/**
 * AdoraCare - back-half clinical workbench view model
 * (BACK-HALF-CLINICAL-WORKBENCH-UI-01).
 *
 * Pure, React-free helpers + types + the role-only access decision for the
 * operator-facing clinical workbench. Kept side-effect-free (no React, no CSS,
 * no value-import from a .tsx at runtime) so the vitest suite can exercise every
 * decision / parse / mapping by direct unit call and render the presentational
 * component via react-dom/server (this repo has NO jsdom; see
 * ClinicalWorkbenchPage.test.tsx). Mirrors workboard-view.ts.
 *
 * HARD DATA RULE (per the package): every affordance is built ONLY from what the
 * back-half endpoints actually accept / return:
 *   - the THREE categorical outcome events (advised / accepted / declined) and
 *     the optional 8-token decline reason that POST /api/appointments/:id/
 *     {treatment-advised,treatment-accepted,treatment-declined} accept;
 *   - the positive-conversion stage moves (coc -> patient, owner ->
 *     converted_to_revenue) that POST /api/leads/:id/stage accepts;
 *   - the owner's own follow-ups from GET /api/me/work-actions (reused panel).
 * It records NO free-text clinical note, NO attachment, NO prescription, NO
 * per-consultation cash - none of those have an endpoint. chief complaint +
 * treatment category have NO appointment-scoped column today, so the UI captures
 * them as on-screen context but NEVER pretends to save them (see
 * UNSAVED_CONTEXT_NOTE). Build to the contract, not the wishlist.
 *
 * NOT an EHR: this records the OPERATIONAL outcome on test/sandbox data. The
 * clinical record of truth lives in EHNote (see SANDBOX_BANNER_TEXT).
 */

import type { DashboardMe } from '@/dashboard/types';
import type {
  AdvanceLeadStageResult,
  AppointmentActionResultView,
  BackHalfLeadStageTarget,
} from '@/dashboard/api';

// --- Role-only access decision (mirrors decideWorkboardAccess) -------------

/**
 * The roles the backend lets RECORD a categorical consultation outcome - the
 * structural mirror of APPOINTMENT_OUTCOME_ROLES (coc / doctor). Both may record
 * outcomes server-side; the VIEW routing (workbenchViewForRole) sends the doctor
 * to the case-sheet COCKPIT (which records the outcome as part of finalize) and
 * the coc to the standalone categorical recorder (the coc's real cockpit is 2C).
 */
export const OUTCOME_ROLES: ReadonlySet<DashboardMe['role']> =
  new Set<DashboardMe['role']>(['coc', 'doctor']);

/**
 * The role that confirms the REVENUE conversion - the structural mirror of the
 * backend STAGE_CONTRACTS.converted_to_revenue ownerRoleKey ('owner'). Revenue
 * is a separate fact from the clinical outcome, so the owner sees a distinct
 * view (the revenue panel + follow-up visibility), never the outcome recorder.
 */
export const REVENUE_ROLES: ReadonlySet<DashboardMe['role']> =
  new Set<DashboardMe['role']>(['owner']);

/** Any role that may reach the workbench route at all (the union of the above). */
export const CLINICAL_WORKBENCH_ROLES: ReadonlySet<DashboardMe['role']> =
  new Set<DashboardMe['role']>(['coc', 'doctor', 'owner']);

/**
 * Which view a role gets:
 *   - 'case_sheet' : the DOCTOR case-sheet cockpit (SANDBOX-CASE-SHEET-WORKBENCH-
 *     V1 / 2B) - persists the #181 clinical spine AND records the consultation
 *     outcome on finalize;
 *   - 'outcome'    : the coc categorical outcome recorder (#178; unchanged);
 *   - 'revenue'    : the owner revenue + follow-up panel (#178; unchanged);
 *   - null         : not a workbench role.
 * The coc / receptionist / owner cockpit SCREENS are the NEXT package (2C); 2B
 * only builds the doctor vertical, so only the doctor's view changes.
 */
export type ClinicalWorkbenchView = 'case_sheet' | 'outcome' | 'revenue';

/** Pure: the view for a role (null = not a workbench role). The DOCTOR now gets
 *  the case-sheet cockpit; the coc keeps the categorical recorder (checked
 *  AFTER the doctor branch so it never reclaims the doctor). */
export function workbenchViewForRole(
  role: DashboardMe['role'],
): ClinicalWorkbenchView | null {
  if (role === 'doctor') return 'case_sheet';
  if (OUTCOME_ROLES.has(role)) return 'outcome';
  if (REVENUE_ROLES.has(role)) return 'revenue';
  return null;
}

export type WorkbenchAccessDecision =
  | { kind: 'wait' }
  | { kind: 'render' }
  | { kind: 'redirect'; to: string };

/**
 * Pure access decision for the route guard. Mirrors decideWorkboardAccess: wait
 * while loading; redirect to /login on error or no scope; redirect to the
 * caller's own homePath when the role is outside the workbench set; else render.
 */
export function decideClinicalWorkbenchAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): WorkbenchAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if (!CLINICAL_WORKBENCH_ROLES.has(state.me.role)) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}

// --- Categorical outcome vocabulary ----------------------------------------

/** The THREE categorical outcomes this UI records (NOT lead_converted - that is
 * a separate, currently-unwired appointment event; the owner revenue fact is a
 * stage move). Matches the backend treatment-advised/accepted/declined routes. */
export type OutcomeAction =
  | 'treatment_advised'
  | 'treatment_accepted'
  | 'treatment_declined';

/** Operator-facing button labels for each categorical outcome. */
export const OUTCOME_ACTION_LABELS: Readonly<Record<OutcomeAction, string>> = {
  treatment_advised: 'Treatment Advised',
  treatment_accepted: 'Treatment Accepted',
  treatment_declined: 'Treatment Declined',
};

// --- Decline reason recoverability -----------------------------------------

/**
 * The recoverable decline reasons - the structural mirror of the backend
 * RECOVERABLE_OUTCOME_REASONS (lib/back-half-outcome-followup-hook.ts, OQ-1
 * founder-ratified). A treatment_declined carrying one of these spawns the
 * PIECE 3 recovery follow-up server-side. The other five controlled reasons
 * (location / chose_competitor / not_interested / duplicate / other) do not.
 */
export const RECOVERABLE_DECLINE_REASONS: ReadonlySet<string> =
  new Set<string>(['price', 'timing', 'no_response']);

/** True only for a recoverable controlled decline reason. */
export function isRecoverableDeclineReason(reason: string | null): boolean {
  return reason !== null && RECOVERABLE_DECLINE_REASONS.has(reason);
}

/**
 * The note to surface AFTER a treatment_declined, derived honestly:
 *   - the outcome event must have durably persisted (`eventEmitted`, the only
 *     signal the response actually returns); the follow-up hook runs ONLY then;
 *   - the chosen reason must be recoverable (a deterministic, client-known set).
 * The response does NOT echo the work-item itself, so this states the documented
 * deterministic server behavior, not a confirmed row. Returns null otherwise.
 */
export function declineFollowupNote(
  reason: string | null,
  eventEmitted: boolean,
): string | null {
  if (eventEmitted && isRecoverableDeclineReason(reason)) {
    return 'This is a recoverable decline, so a recovery follow-up has been queued for this lead.';
  }
  return null;
}

// --- Id parsing (pure) ------------------------------------------------------

/**
 * Parse an operator-typed id into a positive integer, or null when it is blank /
 * non-numeric / not a positive integer. Used to gate a write BEFORE any API call
 * (an honest pre-API validation, never a fake save). Accepts a leading '#'.
 */
export function parsePositiveIntId(raw: string): number | null {
  const trimmed = raw.trim().replace(/^#/, '');
  if (trimmed === '' || !/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 1 ? n : null;
}

// --- Error mapping ----------------------------------------------------------

/** Pull an HTTP status off an unknown error without importing PortalApiError. */
function errorStatus(error: unknown): number | null {
  if (error !== null && typeof error === 'object' && 'status' in error) {
    const s = (error as { status?: unknown }).status;
    if (typeof s === 'number') return s;
  }
  return null;
}

/** Pull the stable machine `code` off an unknown error (PortalApiError carries it). */
function errorCode(error: unknown): string | null {
  if (error !== null && typeof error === 'object' && 'code' in error) {
    const c = (error as { code?: unknown }).code;
    if (typeof c === 'string') return c;
  }
  return null;
}

export interface WorkbenchErrorMessage {
  readonly text: string;
  /** A concurrency conflict (409 lead_stage_conflict) -> offer a Refresh affordance. */
  readonly showRefresh: boolean;
}

/**
 * Human-readable message for a failed outcome / stage call, branching on the
 * backend's STABLE machine code (never substring-matching the message):
 *   - `appointment_not_actionable` (409): the appointment is not fulfilled;
 *   - `lead_stage_conflict` (or any other 409): a concurrency race -> Refresh;
 *   - 401: a role-authority failure;
 *   - else the backend's own PHI-free message, with a safe generic fallback.
 */
export function workbenchErrorMessage(error: unknown): WorkbenchErrorMessage {
  const code = errorCode(error);
  const status = errorStatus(error);
  if (code === 'appointment_not_actionable') {
    return {
      text: 'This appointment is not in a fulfilled state, so an outcome cannot be recorded. Mark the appointment fulfilled first.',
      showRefresh: false,
    };
  }
  if (code === 'lead_stage_conflict' || status === 409) {
    return {
      text: 'This lead was just updated by someone else. Refresh and try again.',
      showRefresh: true,
    };
  }
  if (status === 401) {
    return {
      text: 'Your role is not authorized for this action.',
      showRefresh: false,
    };
  }
  if (error instanceof Error && error.message) {
    return { text: error.message, showRefresh: false };
  }
  return { text: 'Something went wrong. Please try again.', showRefresh: false };
}

// --- Fixed copy -------------------------------------------------------------

/** The prominent sandbox / test-data banner (verbatim package wording). */
export const SANDBOX_BANNER_TEXT =
  'Sandbox - synthetic test data only. The clinical record of truth lives in EHNote. This records the operational outcome.';

/**
 * The honest note shown beside the chief-complaint + treatment-category inputs:
 * there is NO appointment-scoped backend field for either today, so the UI
 * captures them as on-screen context but does NOT persist them (HARD DATA RULE -
 * no affordance that pretends to save what the backend cannot).
 */
export const UNSAVED_CONTEXT_NOTE =
  'Chief complaint and treatment category are captured here as on-screen context only - the backend has no field for them yet, so they are NOT saved (a planned follow-up). The recorded outcome below is what persists.';

// --- Feedback + bound types -------------------------------------------------

export interface WorkbenchFeedback {
  readonly kind: 'success' | 'error';
  /** One or more lines (the cascade surfaces outcome-ok + stage-error as two). */
  readonly lines: readonly string[];
}

/** The appointment the recorder is bound to after the first successful write. */
export interface BoundAppointment {
  readonly appointmentId: number;
  readonly leadId: number | null;
  readonly status: string;
}

/** Map a successful write's echoed appointment into the bound context. Pure. */
export function boundFrom(res: AppointmentActionResultView): BoundAppointment {
  return {
    appointmentId: res.appointment.id,
    leadId: res.appointment.leadId,
    status: res.appointment.status,
  };
}

// --- Outcome orchestration (pure; injected async fns) ----------------------
//
// The cascade + partial-failure logic lives here as a pure, dependency-injected
// async function so vitest can exercise every path (advised / accepted+cascade /
// declined+reason / not-actionable / partial-failure) by direct call with mock
// fns - this repo has NO jsdom, so behavior is tested here, not via clicks.

export interface OutcomeRecordDeps {
  readonly markTreatmentAdvised: (
    appointmentId: number,
  ) => Promise<AppointmentActionResultView>;
  readonly markTreatmentAccepted: (
    appointmentId: number,
  ) => Promise<AppointmentActionResultView>;
  readonly markTreatmentDeclined: (
    appointmentId: number,
    reason?: string,
  ) => Promise<AppointmentActionResultView>;
  readonly advanceLeadStageBackHalf: (
    leadId: number,
    toStage: BackHalfLeadStageTarget,
  ) => Promise<AdvanceLeadStageResult>;
}

export interface RecordOutcomeInput {
  readonly action: OutcomeAction;
  readonly appointmentId: number;
  /** Controlled OUTCOME_REASONS token, or '' for no reason (treatment_declined only). */
  readonly declineReason: string;
  /** Pre-resolved display label for the reason (the caller owns the label map). */
  readonly declineReasonLabel: string;
  /**
   * Whether the caller may advance the lead into `patient` on the accepted
   * cascade. ONLY coc owns the `patient` stage (STAGE_CONTRACTS), so a doctor -
   * who CAN record the outcome but is NOT authorized for that stage - passes
   * false: the outcome is recorded and the UI states COC advances the lead,
   * instead of firing a request we know the backend would reject with 401.
   */
  readonly advancePatientStage: boolean;
}

export interface OutcomeRecordOutput {
  readonly feedback: WorkbenchFeedback;
  /** The appointment to bind to (only set when a write succeeded). */
  readonly bound: BoundAppointment | null;
}

/**
 * Record ONE categorical outcome. For `treatment_accepted` this is the cascade:
 * record the outcome, THEN advance the lead to `patient` - two SEPARATE domain
 * events behind one operator click. A partial failure (outcome ok, stage fails)
 * returns a kind:'error' feedback whose FIRST line still states the outcome was
 * saved (never swallowed). A null leadId on the appointment is reported, not
 * forced. The function itself never throws - every rejection becomes feedback.
 */
export async function recordOutcome(
  deps: OutcomeRecordDeps,
  input: RecordOutcomeInput,
): Promise<OutcomeRecordOutput> {
  const { action, appointmentId } = input;

  if (action === 'treatment_advised') {
    try {
      const res = await deps.markTreatmentAdvised(appointmentId);
      return {
        bound: boundFrom(res),
        feedback: {
          kind: 'success',
          lines: [`Treatment advised recorded for appointment #${appointmentId}.`],
        },
      };
    } catch (err) {
      return { bound: null, feedback: errorFeedback(err) };
    }
  }

  if (action === 'treatment_declined') {
    const reason = input.declineReason || undefined;
    try {
      const res = await deps.markTreatmentDeclined(appointmentId, reason);
      const lines = [
        `Treatment declined recorded for appointment #${appointmentId}${
          reason ? ` (reason: ${input.declineReasonLabel || reason})` : ''
        }.`,
      ];
      const note = declineFollowupNote(
        reason ?? null,
        res.appointmentEventEmitted,
      );
      if (note) lines.push(note);
      return { bound: boundFrom(res), feedback: { kind: 'success', lines } };
    } catch (err) {
      return { bound: null, feedback: errorFeedback(err) };
    }
  }

  // treatment_accepted: the cascade.
  let acceptedRes: AppointmentActionResultView;
  try {
    acceptedRes = await deps.markTreatmentAccepted(appointmentId);
  } catch (err) {
    return { bound: null, feedback: errorFeedback(err) };
  }
  const bound = boundFrom(acceptedRes);
  const lines = [`Treatment accepted recorded for appointment #${appointmentId}.`];
  const cascadeLeadId = acceptedRes.appointment.leadId;
  if (cascadeLeadId === null) {
    lines.push(
      'This appointment has no linked lead, so the stage was not advanced to Patient.',
    );
    return { bound, feedback: { kind: 'success', lines } };
  }
  if (!input.advancePatientStage) {
    // The caller (a doctor) records the outcome but does NOT own the `patient`
    // stage - that is a COC action (STAGE_CONTRACTS). Report it plainly rather
    // than firing a request the backend would reject.
    lines.push(
      'Advancing the lead to Patient is a COC action - the outcome is recorded; ask COC to advance the lead.',
    );
    return { bound, feedback: { kind: 'success', lines } };
  }
  try {
    await deps.advanceLeadStageBackHalf(cascadeLeadId, 'patient');
    lines.push(`Lead #${cascadeLeadId} moved to Patient.`);
    return { bound, feedback: { kind: 'success', lines } };
  } catch (stageErr) {
    // PARTIAL FAILURE: the outcome IS saved; only the stage advance failed.
    lines.push(
      `Outcome saved, but the stage advance to Patient failed: ${
        workbenchErrorMessage(stageErr).text
      }`,
    );
    return { bound, feedback: { kind: 'error', lines } };
  }
}

/** Confirm the revenue conversion (owner). Never throws - returns feedback. */
export async function confirmRevenue(
  deps: Pick<OutcomeRecordDeps, 'advanceLeadStageBackHalf'>,
  leadId: number,
): Promise<WorkbenchFeedback> {
  try {
    await deps.advanceLeadStageBackHalf(leadId, 'converted_to_revenue');
    return {
      kind: 'success',
      lines: [`Lead #${leadId} converted to revenue. Case closed.`],
    };
  } catch (err) {
    return errorFeedback(err);
  }
}

/** A single-line error feedback from any rejection. */
function errorFeedback(err: unknown): WorkbenchFeedback {
  return { kind: 'error', lines: [workbenchErrorMessage(err).text] };
}
